import React, { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import DynamicFieldsGrid, { renderPaymentItem } from './DynamicFieldsGrid';
import ControlledField from './ControlledField';
import FloatingNumber from '../../../../components/input/FloatingNumber';
import { formatCurrency } from '../../../../utils/format';

export default function CompensationFields({ control, FloatingInput, fields = null, cleanNumber, parentName = null, getValues, setValue, salesPriceFormattedTick, handleSellerCalculation }) {
  const data = Array.isArray(fields) && fields.length ? fields : null;

  const baseParent = parentName || 'compensationMeta';

  // Watch salesPrice so we can react to programmatic updates (e.g. calSellerWant)
  // even when the blur-driven `salesPriceFormattedTick` does not change.
  const watchedSalesPrice = useWatch({ control, name: 'salesPrice' });
  const lastProcessedRef = useRef({ tick: null, salesPrice: null });

  const isStableSalesPriceString = (v) => {
    const s = String(v ?? '').trim();
    // Programmatic updates typically use toFixed(2) (e.g. "123456.78").
    // Keep it permissive for formatted strings as well.
    return /^-?\$?[0-9][0-9,]*\.[0-9]{2}$/.test(s);
  };

  const computeAndSetTotal = () => {
    try {
      if (typeof getValues !== 'function' || typeof setValue !== 'function') return 0;
      const arr = getValues(baseParent) || [];
      let sum = 0;
      if (Array.isArray(arr)) {
        for (const it of arr) {
          if (!it) continue;
          const raw = it.value ?? it.amount ?? '0';
          const num = Number(String(raw).replace(/[^0-9.-]+/g, '')) || 0;
          sum += num;
        }
      }
      const formatted = formatCurrency(sum);
      try { setValue(`${baseParent}Total`, formatted, { shouldDirty: true, shouldTouch: false }); } catch (e) { /* ignore */ }
      return sum;
    } catch (e) {
      return 0;
    }
  };

  // When a rate is formatted, recalc the dollar amount for that index
  const onRateFormatted = (origIndex) => {
    try {
      if (typeof getValues !== 'function' || typeof setValue !== 'function') return;
      //console.log('Recalculating compensation amount for index:', origIndex);
      const ratePath = `${parentName}[${origIndex}].rate`;
      const rateRaw = getValues(ratePath);
      const salesRaw = getValues('salesPrice') || '0';
      const sales = Number(String(salesRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const rate = Number(String(rateRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const amount = Number.isFinite(sales * (rate / 100)) ? sales * (rate / 100) : 0;
      const formatted = formatCurrency(amount);
      setValue(`${parentName}[${origIndex}].value`, formatted, { shouldDirty: true, shouldValidate: false });
      // update total compensation
      computeAndSetTotal();
      handleSellerCalculation && handleSellerCalculation();
    } catch (e) {
      // ignore
    }
  };

  const recalcAllFromSalesPrice = () => {
    try {
      if (typeof getValues !== 'function' || typeof setValue !== 'function') return;

      const salesRaw = getValues('salesPrice') || '0';
      const sales = Number(String(salesRaw).replace(/[^0-9.-]+/g, '')) || 0;

      // Recalc each row's amount from its rate, but only call total + seller calc once.
      for (let origIndex = 0; origIndex < (data || []).length; origIndex++) {
        const ratePath = `${baseParent}[${origIndex}].rate`;
        const rateRaw = getValues(ratePath);
        const rate = Number(String(rateRaw).replace(/[^0-9.-]+/g, '')) || 0;
        const amount = Number.isFinite(sales * (rate / 100)) ? sales * (rate / 100) : 0;
        const formatted = formatCurrency(amount);
        setValue(`${baseParent}[${origIndex}].value`, formatted, { shouldDirty: true, shouldValidate: false });
      }

      computeAndSetTotal();
      handleSellerCalculation && handleSellerCalculation();
    } catch (e) {
      // ignore
    }
  };

  // When an amount/value is formatted, recalc the rate for that index
  const onAmountFormatted = (origIndex) => {
    try {
      if (typeof getValues !== 'function' || typeof setValue !== 'function') return;
      const valuePath = `${parentName}[${origIndex}].value`;
      const ratePath = `${parentName}[${origIndex}].rate`;
      const valueRaw = getValues(valuePath) || '0';
      const salesRaw = getValues('salesPrice') || '0';
      const sales = Number(String(salesRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const amount = Number(String(valueRaw).replace(/[^0-9.-]+/g, '')) || 0;
      const rate = sales > 0 ? (amount / sales) * 100 : 0;
      // round to 6 decimal places max then trim trailing zeros when formatted by consumer
      const rateRounded = Math.round((rate + Number.EPSILON) * 1000000) / 1000000;
      setValue(ratePath, rateRounded, { shouldDirty: true, shouldValidate: false });
      // update total compensation
      computeAndSetTotal();
      handleSellerCalculation && handleSellerCalculation();
    } catch (e) {
      // ignore
    }
  };

  // When salesPrice is formatted (blur), salesPriceFormattedTick increments and triggers recalculation
  useEffect(() => {
    if (typeof getValues !== 'function' || typeof setValue !== 'function') return undefined;
    try {
      recalcAllFromSalesPrice();
      lastProcessedRef.current = { tick: salesPriceFormattedTick ?? null, salesPrice: getValues('salesPrice') ?? null };
    } catch (e) {
      // ignore
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesPriceFormattedTick, getValues, setValue, parentName]);

  // When salesPrice changes programmatically (without blur), ensure compensation updates every time.
  useEffect(() => {
    try {
      if (!data || !data.length) return;

      const last = lastProcessedRef.current || {};
      // If the blur tick changed, the tick-effect will handle it.
      if ((salesPriceFormattedTick ?? null) !== (last.tick ?? null)) return;

      const currentSales = watchedSalesPrice;
      if (currentSales === last.salesPrice) return;
      // Avoid recalculating on transient/partial typing; focus on stable values.
      if (!isStableSalesPriceString(currentSales)) return;

      recalcAllFromSalesPrice();
      lastProcessedRef.current = { tick: salesPriceFormattedTick ?? null, salesPrice: currentSales ?? null };
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedSalesPrice, salesPriceFormattedTick]);

  if (!data) return null;

  return (
    <>
      <DynamicFieldsGrid
        fields={data}
        control={control}
        FloatingInput={FloatingInput}
        cleanNumber={cleanNumber}
        renderItem={(item, ctx) => renderPaymentItem(item, {
          ...ctx,
          index: ctx.index,
          parentName: parentName,
          // ControlledField will call onFormatted(name, formatted)
          // parse the index from the name (e.g. 'compensationMeta[2].rate') and trigger onRateFormatted
          onFormatted: (name, formatted) => {
            try {
              if (!name || typeof name !== 'string') return;
              const m = name.match(/\[(\d+)\]/);
              const idx = m ? Number(m[1]) : ctx.index;
              // determine whether the formatted field was rate or value and call appropriate handler
              if (name.endsWith('.rate')) {
                onRateFormatted(idx);
              } else if (name.endsWith('.value') || name.endsWith('.amount')) {
                onAmountFormatted(idx);
              } else {
                // default: recalc amount from rate
                onRateFormatted(idx);
              }
            } catch (e) {
              // ignore
            }
          }
        })}
        fullRowPerItem={true}
        parentName={parentName}
        getRowClass={(pair) => {
          // if any item in the row is a day-entry, use symbol-wrap class
          if (Array.isArray(pair) && pair.some((it) => it.day !== undefined && it.day !== null && it.day !== '')) return 'calc-frm-field symbol-wrap';
          return 'calc-frm-field full-dvd';
        }}
        getValues={getValues}
        setValue={setValue}
      />

      {/* Total compensation row: label under rate column (left), disabled total input under amount column (right) */}
      <div className="calc-frm-field full-dvd">
        <div className="calc-frm-fld form-group">
          <label className="lbl-section txt-bold">Total Compensation</label>
        </div>
        <div className="calc-frm-fld form-group sign-of-amount">
          <ControlledField
            name={`${baseParent}Total`}
            control={control}
            defaultValue={getValues ? getValues(`${baseParent}Total`) : ''}
            InputComponent={FloatingInput || FloatingNumber}
            placeholder=""
            type="text"
            cleanNumber={cleanNumber}
            disabled={true}
            onFormatted={(name, value) => { /* noop */ }}
            inputProps={{ disabled: true }}
          />
        </div>
      </div>
    </>
  );
}
